import axios from 'axios';

/**
 * @fileoverview Modulo per le chiamate API dell'utente finale.
 * Queste API non richiedono un 'client_id' esplicito nel percorso URL,
 * poiché l'identificazione del cliente avviene tramite il token JWT.
 * Include anche le API di autenticazione.
 */

const API_BASE_URL = process.env.REACT_APP_API_MODE === 'remote'
  ? 'https://digitalsignageapi.sistemiposreport.store'
  : 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Intercettore per le richieste HTTP che aggiunge il token di autenticazione
 * all'header `Authorization` se presente nel `localStorage`.
 * Gestisce anche l'Anti-Caching.
 */
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // --- BLOCCARE LA CACHE ---
    // 1. Header standard per dire al browser e ai proxy di non cachare
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    // 2. "Cache busting" brutale per le richieste GET:
    // Aggiunge un parametro _t=TIMESTAMP all'URL. 
    // Poiché il timestamp cambia ogni millisecondo, il browser è costretto
    // a considerare ogni richiesta come una nuova URL.
    if (config.method === 'get') {
      config.params = config.params || {};
      config.params['_t'] = Date.now();
    }
    // -------------------------

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Costruisce l'URL completo per una risorsa che NON richiede un `client_id` nel percorso.
 * @param {string} resource - La risorsa API (es. 'login', 'media').
 * @param {string|number|null} [id] - L'ID specifico dell'elemento.
 * @returns {string} L'URL completo dell'endpoint.
 */
const getUserUrl = (resource, id = null) => {
  let url = `/${resource}`;
  if (id) {
    url += `/${id}`;
  }
  return url;
};

/**
 * Funzioni CRUD generiche per endpoint che **NON richiedono** un client_id nel percorso.
 */
const generalApi = {
  /**
   * Recupera una lista di elementi da una risorsa.
   * @param {string} resource - La risorsa da cui recuperare i dati.
   * @returns {Promise<object[]>} La lista di elementi.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: async (resource, { queryParams } = {}) => {
    try {
      const url = getUserUrl(resource);
      const response = await api.get(url, { params: queryParams });
      console.log(`Fetched items from resource "${resource}":`, response.data);
      return response;
    } catch (error) {
      console.error(`Error fetching items from resource "${resource}":`, error);
      throw error;
    }
  },
  /**
   * Crea un nuovo elemento su una risorsa.
   * @param {string} resource - La risorsa su cui creare l'elemento.
   * @param {object} data - I dati del nuovo elemento.
   * @returns {Promise<object>} L'elemento creato.
   * @throws {Error} Se la richiesta fallisce.
   */
  create: async (resource, data) => {
    try {
      const url = getUserUrl(resource);
      const response = await api.post(url, data);
      return response;
    } catch (error) {
      console.error(`Error creating item on resource "${resource}":`, error);
      throw error;
    }
  },
  /**
   * Aggiorna un elemento su una risorsa.
   * @param {string} resource - La risorsa da aggiornare.
   * @param {string|number} id - L'ID dell'elemento da aggiornare.
   * @param {object} data - I dati aggiornati.
   * @returns {Promise<object>} L'elemento aggiornato.
   * @throws {Error} Se la richiesta fallisce.
   */
  update: async (resource, id, data) => {
    try {
      const url = getUserUrl(resource, id);
      const response = await api.put(url, data);
      return response;
    } catch (error) {
      console.error(`Error updating item ${id} on resource "${resource}":`, error);
      throw error;
    }
  },
  /**
   * Elimina un elemento da una risorsa.
   * @param {string} resource - La risorsa da cui eliminare l'elemento.
   * @param {string|number} id - L'ID dell'elemento da eliminare.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  delete: async (resource, id) => {
    try {
      const url = getUserUrl(resource, id);
      const response = await api.delete(url);
      return response;
    } catch (error) {
      console.error(`Error deleting item ${id} on resource "${resource}":`, error);
      throw error;
    }
  },
};

/**
 * Modulo API per la gestione dei clienti e l'autenticazione.
 */
export const clientsApi = {
  /**
   * Recupera la lista di tutti i clienti. (Potrebbe essere solo per l'admin, ma mantenuto qui se l'utente finale ha un endpoint di listing)
   * @returns {Promise<object[]>} La lista dei clienti.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('clients'),

  /**
   * Esegue il login di un cliente.
   * @param {object} payload - Credenziali di login.
   * @returns {Promise<object>} Il token di autenticazione e i dati del cliente.
   * @throws {Error} Se la richiesta fallisce (es. credenziali errate).
   */
  login: (payload) => api.post('/login', payload),
};

/**
 * Modulo API per la gestione dei dispositivi.
 */
export const deviceApi = {
  /**
   * Recupera tutti i dispositivi del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei dispositivi.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('devices'),

};

/**
 * Modulo API per la gestione dei negozi.
 */
export const storeApi = {
  /**
   * Recupera tutti i negozi del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei negozi.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('stores'),
};

/**
 * Modulo API per la gestione dei gruppi di negozi.
 */
export const storeGroupApi = {
  /**
   * Recupera tutti i gruppi di negozi del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei gruppi di negozi.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('store_groups'),
};

/**
 * Modulo API per la gestione dei gruppi di dispositivi.
 */
export const deviceGroupApi = {
  /**
   * Recupera tutti i gruppi di dispositivi del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei gruppi di dispositivi.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('device_groups'),
};

/**
 * Modulo API per la gestione dei tipi di dispositivo.
 */
export const deviceTypeApi = {
  /**
   * Recupera tutti i tipi di dispositivo del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei tipi di dispositivo.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: () => generalApi.get('device_types'),
};

/**
 * Modulo API per la gestione dei content set.
 */
export const contentSetApi = {
  /**
   * Recupera tutti i content set del cliente autenticato.
   * @returns {Promise<object[]>} La lista dei content set.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: (mediaMode) => generalApi.get('content_sets', {queryParams: { mediaMode }}),
  /**
   * Crea un nuovo content set per il cliente autenticato.
   * @param {object} data - I dati del nuovo content set.
   * @returns {Promise<object>} Il content set creato.
   * @throws {Error} Se la richiesta fallisce.
   */
  create: (data) => generalApi.create('content_sets', data),
};

/**
 * Modulo API per la gestione della configurazione del monitor.
 */
export const monitorConfigurationApi = {
  /**
   * Recupera la configurazione del monitor del cliente autenticato.
   * @returns {Promise<object>} La configurazione del monitor.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: async () => generalApi.get('configuration/monitor'),
  /**
   * Aggiorna la configurazione del monitor del cliente autenticato.
   * @param {object} data - I dati di configurazione da aggiornare.
   * @returns {Promise<object>} La configurazione aggiornata.
   * @throws {Error} Se la richiesta fallisce.
   */
  update: async (data) => generalApi.update('configuration/monitor', null, data),

  /**
   * Conferma la configurazione del monitor del cliente autenticato.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  confirm: async () => generalApi.get('configuration/monitor/confirm'),

  /**
   * Conferma la cancellazione della configurazione del monitor del cliente autenticato.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  delete: async () => generalApi.get('configuration/monitor/confirm/delete')
};


export const totemConfigurationApi = {
  /**
   * Recupera la configurazione del totem del cliente autenticato.
   * @returns {Promise<object>} La configurazione del totem.
   * @throws {Error} Se la richiesta fallisce.
   */
  get: async () => generalApi.get('configuration/totem'),
  /**
   * Aggiorna la configurazione del totem del cliente autenticato.
   * @param {object} data - I dati di configurazione da aggiornare.
   * @returns {Promise<object>} La configurazione aggiornata.
   * @throws {Error} Se la richiesta fallisce.
   */
  update: async (data) => generalApi.update('configuration/totem', null, data),

  /**
   * Conferma la configurazione del totem del cliente autenticato.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  confirm: async () => generalApi.get('configuration/totem/confirm'),

  /**
   * Conferma la cancellazione della configurazione del totem del cliente autenticato.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  delete: async () => generalApi.get('configuration/totem/confirm/delete')
};

/**
 * Modulo API per la gestione delle playlist.
 */
export const playlistApi = {
  /**
   * Aggiorna una playlist associata a un'immagine del cliente autenticato.
   * @param {string|number} img_id - L'ID dell'immagine.
   * @param {object} data - I dati della playlist da aggiornare.
   * @returns {Promise<object>} La playlist aggiornata.
   * @throws {Error} Se la richiesta fallisce.
   */
  updatePlaylist: async (img_id, data) => {
    try {
      const response = await api.put(`/playlist/${img_id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating playlist:', error);
      throw error;
    }
  },
};

/**
 * Modulo API per la gestione dei media (immagini, video, ecc.).
 */
export const mediaApi = {
  /**
   * Estrae immagini da un file PDF per il cliente autenticato.
   * @param {string} mode - La modalità di estrazione ('perPage' o 'perImage').
   * @param {File} pdfFile - Il file PDF da processare.
   * @returns {Promise<object>} La risposta dell'API con le immagini estratte.
   * @throws {Error} Se la richiesta fallisce.
   */
  extractFromPdf: async (mode, pdfFile) => {
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      const url = getUserUrl(`pdf/extract/${mode}`);
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      console.error('Error extracting images from PDF:', error);
      throw error;
    }
  },
  /**
   * Aggiorna la pianificazione (schedule) per un media del cliente autenticato.
   * @param {string|number} media_id - L'ID del media.
   * @param {object} data - I dati di pianificazione da aggiornare.
   * @returns {Promise<boolean>} `true` se l'operazione ha successo, `false` altrimenti.
   * @throws {Error} Se la richiesta fallisce.
   */
  updateSchedule: async (media_id, data) => {
    try {
      const url = getUserUrl(`media/${media_id}/schedule`);
      const response = await api.post(url, data);
      return response.status === 200;
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  },
  /**
   * Aggiorna la playlist per un media del cliente autenticato.
   * @param {string|number} media_id - L'ID del media.
   * @param {object} data - I dati della playlist da aggiornare.
   * @returns {Promise<boolean>} `true` se l'operazione ha successo, `false` altrimenti.
   * @throws {Error} Se la richiesta fallisce.
   */
  updatePlaylist: async (media_id, data) => {
    try {
      const url = getUserUrl(`media/${media_id}/playlist`);
      const response = await api.post(url, data);
      return response.status === 200;
    } catch (error) {
      console.error('Error updating playlist:', error);
      throw error;
    }
  },
  /**
   * Carica un singolo media con i suoi metadati per il cliente autenticato.
   * @param {object} data - Dati contenenti il file e i metadati.
   * @param {File} data.file - Il file del media.
   * @param {number} data.content_set_id - L'ID del content set.
   * @returns {Promise<object>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  upload: async (data) => {
    try {
      const formData = new FormData();
      console.log('Uploading media with metadata:', data.uploadMetadata);
      formData.append('file', data.file);
      formData.append('metadata', JSON.stringify(data.uploadMetadata));
      const url = getUserUrl('media');
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  },
  /**
   * Elimina un media del cliente autenticato.
   * @param {string|number} media_id - L'ID del media.
   * @returns {Promise<any>} La risposta dell'API.
   * @throws {Error} Se la richiesta fallisce.
   */
  delete: async (media_id) => {
    try {
      const url = getUserUrl('media', media_id);
      const response = await api.delete(url);
      return response;
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  },
};